/**
 * SOH bucket migration: which unit level(s) the chart counts. Pure helpers.
 *
 * Org meta: metaInfo.BatteryIntelligence.SOHAndRUL.charts.sohMigrationSankeyChart.levels,
 * an ordered list of "battery" | "pack" | "string" (the first is selected by default).
 */
import { SOH_HISTORY_LEVELS } from "./sohHistoryFields";

export const DEFAULT_SOH_MIGRATION_LEVELS = Object.freeze(["battery"]);

export const SOH_LEVEL_LABELS = Object.freeze({ battery: "Battery", pack: "Pack", string: "String" });

/**
 * Org meta `levels` -> ordered, valid, unique, lower-case levels. Non-string or
 * unknown entries are dropped (case and surrounding spaces are ignored); an empty or
 * missing list gives ["battery"].
 */
export function resolveSohMigrationLevels(value) {
  const out = [];
  (Array.isArray(value) ? value : []).forEach((entry) => {
    if (typeof entry !== "string") return;
    const level = entry.trim().toLowerCase();
    if (SOH_HISTORY_LEVELS.includes(level) && !out.includes(level)) out.push(level);
  });
  return out.length ? out : [...DEFAULT_SOH_MIGRATION_LEVELS];
}

/** The level toggle is shown only when there is a choice. */
export const shouldShowLevelToggle = (levels) => Array.isArray(levels) && levels.length > 1;

/** Default selection: the first configured level. */
export const getDefaultLevel = (levels) => (Array.isArray(levels) && levels[0]) || "battery";

/**
 * { value, label } options for SegmentedToggle. The battery option uses the org's base
 * entity label (useBaseEntityInfo baseEntityTypeLable), so a vehicle org reads
 * "Vehicle | Pack | String".
 */
export const getLevelToggleOptions = (levels, { baseLabel } = {}) =>
  (Array.isArray(levels) ? levels : []).map((level) => ({
    value: level,
    label: level === "battery" && typeof baseLabel === "string" && baseLabel.trim()
      ? baseLabel
      : SOH_LEVEL_LABELS[level] || level,
  }));

/**
 * A label for running text: lower-cased ("Vehicles" -> "vehicles") unless it holds an
 * acronym (an upper-case run of 2+ letters), which keeps the org's casing
 * ("BESS Units" stays "BESS Units").
 */
export function toRunningTextNoun(label) {
  const text = String(label);
  return /[A-Z]{2,}/.test(text) ? text : text.toLowerCase();
}

/**
 * Nouns for running text at the level: battery uses the org's base entity labels
 * (useBaseEntityInfo), pack / string are fixed. See toRunningTextNoun for casing.
 * @returns {{ singular: string, plural: string }}
 */
export function getSohLevelNouns(level, { baseSingular = "Battery", basePlural = "Batteries" } = {}) {
  if (level === "pack") return { singular: "pack", plural: "packs" };
  if (level === "string") return { singular: "string", plural: "strings" };
  return {
    singular: toRunningTextNoun(baseSingular || "Battery"),
    plural: toRunningTextNoun(basePlural || "Batteries"),
  };
}

/** Drill-down table title and empty message for the level's nouns. Only the first
 * letter of the title is raised, so an acronym is never altered. */
export function getLevelTableTexts({ plural }) {
  const p = String(plural || "batteries");
  return {
    tableTitle: `${p.charAt(0).toUpperCase()}${p.slice(1)} List`,
    emptyMessage: `No ${p} found for the selected SOH category`,
  };
}

/**
 * Org meta table keys for the level's drill-down, most specific first (resolved by
 * pickDrillDownTableSchema, which reads `<path>.table`):
 *   SOHMigrationDrillDown.<level>.table -> SOHMigrationDrillDown.table -> SOHDrillDown.table
 */
export function getSohMigrationTableMetaKeys(level) {
  const lvl = SOH_HISTORY_LEVELS.includes(level) ? level : "battery";
  return [`SOHMigrationDrillDown.${lvl}`, "SOHMigrationDrillDown", "SOHDrillDown"];
}
